import { Injectable } from '@angular/core';
import DisableDevtool from 'disable-devtool';
import { BehaviorSubject } from 'rxjs';
import { delay, distinctUntilChanged, filter, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SecurityStateService } from './security-state.service';
import { SecurityEventsService } from './security-events.service';

@Injectable({
  providedIn: 'root',
})
export class DisableDevtoolsService {
  private readonly initializedChanges = new BehaviorSubject(false);
  private readonly openDevtoolsChanges = new BehaviorSubject(false);

  readonly isInitialized$ = this.initializedChanges.asObservable().pipe(
    filter((initialized) => initialized),
    delay(150),
    distinctUntilChanged(),
    shareReplay(1),
  );

  readonly isDevtoolsOpened$ = this.openDevtoolsChanges.pipe(distinctUntilChanged());

  constructor(
    private readonly router: Router,
    private readonly securityStateService: SecurityStateService,
    private readonly securityEventsService: SecurityEventsService,
  ) {}

  async initialize(): Promise<void> {
    const isEnabled = await this.securityStateService.isEnabled('open-devtools');
    const isMobileYandexBrowser = this.isMobileYandexBrowser();
    const isInitialized = this.initializedChanges.getValue();

    if (!isEnabled || isMobileYandexBrowser || isInitialized) {
      this.initializedChanges.next(true);
      return;
    }

    DisableDevtool({
      interval: 50,
      disableMenu: false,
      disableSelect: false,
      disableInputSelect: false,
      disableCopy: false,
      disableCut: false,
      disablePaste: false,
      clearLog: true,
      stopIntervalTime: 2_000,
      url: 'about:blank',

      ondevtoolopen: (_, next) => {
        this.handleOpenDevtools();
        next();
      },
      ondevtoolclose: () => this.handleCloseDevtools(),
    });

    this.initializedChanges.next(true);
  }

  private async handleOpenDevtools(): Promise<void> {
    if (this.openDevtoolsChanges.getValue()) {
      return;
    }

    this.openDevtoolsChanges.next(true);
    this.sendEvent();

    window.location.replace('about:blank');
    this.router.navigateByUrl('/');
  }

  private handleCloseDevtools(): void {
    this.openDevtoolsChanges.next(false);
  }

  private isMobileYandexBrowser(): boolean {
    const ua = navigator.userAgent;

    const isYandex = /YaBrowser/i.test(ua);

    const isMobile =
      /Android|iPhone|iPad|iPod/i.test(ua) ||
      (navigator.maxTouchPoints > 1 && window.matchMedia('(pointer: coarse)').matches);

    return isYandex && isMobile;
  }

  private sendEvent(): void {
    this.securityEventsService.sendEvent('open-devtools');
  }
}
